<template>
  <div class="flex flex-col items-center px-3" v-if="order.id && !order.empty()">
    <div class="w-full">
      <div class="flex justify-between w-full p-3 card rounded">
        <p>{{ t('customer') }}</p>
        <p class="flex justify-end custom-bold">
          {{ order?.invoice?.customer?.contactDisplayName ?? t('customer') }}
        </p>
      </div>

      <div class="card rounded p-3 mt-4">
        <div class="flex justify-between p-3 w-full bg-blue-100 rounded">
          <p>{{ t('orderNumber') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order?.externalID }}
          </p>
        </div>
        <div class="flex justify-between p-3 w-full rounded">
          <p>{{ t('orderType') }}</p>
          <p class="flex justify-end font-bold">
            <OrderTypeCellRenderer :data="order" :readOnly="true"></OrderTypeCellRenderer>
          </p>
        </div>
        <div v-if="order.orderType == 'delivery'" class="flex justify-between p-3 w-full rounded">
          <p>{{ t('carrier') }}</p>
          <p class="flex justify-end font-bold w-1/2">
            <OrderCarrierCellRenderer :data="order" :readOnly="true"></OrderCarrierCellRenderer>
          </p>
        </div>
        <div class="flex items-center justify-between p-3 w-full rounded">
          <p>{{ t('orderState') }}</p>
          <div class="flex items-center gap-2">
            <FulfillButton 
              v-if="shouldShowFulfillButton"
              :order="order"
              @statusChanged="handleStatusChanged"
            />
            <p class="flex justify-end font-bold w-[140px]">
              <OrderStatusCellRenderer :data="order"></OrderStatusCellRenderer>
            </p>
          </div>
        </div>
        <div
          v-if="order?.invoice.pouchID"
          class="flex justify-between p-3 mt-1 w-full bg-gray-100 rounded"
        >
          <p>{{ t('pouchID') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order?.invoice.pouchID }}
          </p>
        </div>
        <div class="flex justify-between p-3 w-full">
          <p>{{ t('models.common.date') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatPostgresDate(order?.createdAt, 'DD/MM/YYYY hh:mm A') }}
          </p>
        </div>
        <div v-if="order?.fulfilledByUserID" class="flex justify-between p-3 w-full">
          <p>{{ t('fulfilledBy') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order?.fulfilledByUser?.name }}
          </p>
        </div>
        <div class="flex justify-between p-3 w-full">
          <p>{{ t('user') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order?.user.fullName }}
          </p>
        </div>
        <div
          v-if="order?.invoice.salesRep"
          class="flex justify-between p-3 mt-1 w-full"
        >
          <p>{{ t('salesRep') }}</p>
          <p class="flex justify-end custom-bold">
            {{ `${order?.invoice.salesRep.name}` }}
          </p>
        </div>
        <div class="flex justify-between w-full p-3">
          <p>{{ t('location') }}</p>
          <p class="flex justify-end custom-bold">
            {{ $ctx.locale === 'en' ? order?.location?.enName : order?.location?.arName }}
          </p>
        </div>
        <div class="flex justify-between w-full p-3">
          <p>{{ t('register') }}</p>
          <p class="flex justify-end custom-bold">
            {{ $ctx.locale === 'en' ? order?.register?.enName : order?.register?.arName }}
          </p>
        </div>
        <div class="flex justify-between w-full p-3">
          <p>{{ t('orderNotes') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order?.invoice.notes }}
          </p>
        </div>
      </div>

      <div class="card rounded p-3 mt-4">
        <div class="flex justify-between w-full p-3">
          <p>{{ t('paymentTerms') }}</p>
          <p class="flex justify-end custom-bold">
            {{ getPaymentTerm(order) }}
          </p>
        </div>

        <div v-if="order.invoice?.billDueDate" class="flex justify-between w-full p-3">
          <p>{{ t('dueOn') }}</p>
          <p class="flex justify-end custom-bold" :class="dueOnClass">
            {{ formatPostgresDate(String(order.invoice.billDueDate), 'DD/MM/YYYY') }}
          </p>
        </div>

        <div class="flex items-center justify-between p-3 w-full rounded">
          <p>{{ t('customerPaymentState') }}</p>
          <p class="flex justify-end font-bold w-[140px]">
            <PaymentStatusCellRenderer :data="order"></PaymentStatusCellRenderer>
          </p>
        </div>

        <div class="flex items-center justify-between p-3 w-full rounded">
          <p>{{ t('insurancePaymentState') }}</p>
          <p class="flex justify-end font-bold w-[140px]">
            <InsurancePaymentStatusCellRenderer :data="order"></InsurancePaymentStatusCellRenderer>
          </p>
        </div>

        <div v-if="order.invoice?.totalDiscountAmount || calculateTotalItemDiscounts() > 0" class="flex justify-between w-full p-3">
          <p>{{ t('totalBeforeDiscounts') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatMoney(calculateTotalBeforeDiscounts(), "IQD") }}
          </p>
        </div>

        <div v-if="order.invoice?.totalDiscountAmount || calculateTotalItemDiscounts() > 0" class="flex justify-between w-full p-3">
          <p>{{ t('discount') }}</p>
          <p class="flex justify-end custom-bold">
            -{{ formatMoney((order.invoice?.totalDiscountAmount || 0) + calculateTotalItemDiscounts(), "IQD") }}
          </p>
        </div>

        <div v-if="order.invoice?.shippingFeeAmount" class="flex justify-between w-full p-3">
          <p>{{ t('shipping') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatDinero(order.invoice?.shippingFeeDinero) }}
          </p>
        </div>

        <div v-if="order.invoice?.insuranceID" class="flex justify-between w-full p-3">
          <p>{{ t('insurance') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order.invoice?.insurance?.name }}
          </p>
        </div>

        <div v-if="order.invoice?.insuranceID" class="flex justify-between w-full p-3">
          <p>{{ t('insuranceTotalDue') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatDinero(order.invoice?.insuranceTotalDueDinero) }}
          </p>
        </div>

        <div v-if="order.invoice?.insuranceID" class="flex justify-between w-full p-3">
          <p>{{ t('insurancePaidAmount') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatDinero(order.invoice?.insuranceTotalPaidDinero) }}
          </p>
        </div>

        <div class="flex justify-between w-full p-3">
          <p>{{ t('total') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatDinero(order.invoice?.totalDueDinero) }}
          </p>
        </div>

        <div
          v-if="!order.paymentComplete"
          class="flex justify-between w-full p-3 bg-green-100 rounded"
        >
          <p>{{ t('paymentsMade') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatDinero(order.invoice?.totalPaidDinero) }}
          </p>
        </div>
        <div
          v-if="!order.paymentComplete"
          class="flex justify-between w-full p-3 bg-red-100 rounded mt-2"
        >
          <p>{{ t('moneyOwed') }}</p>
          <p class="flex justify-end custom-bold">
            {{ formatDinero(order.invoice?.totalBalanceDinero) }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="card rounded w-full p-3 mt-4"
      v-if="showPrintReceipt || !order.paymentComplete"
    >
      <template v-if="$can('update', '/sales/orders')">
        <div class="mt-1 flex gap-2">
          <div class="flex flex-col items-center w-full gap-1 text-black">
            <horizontal-button isWarning faIcon="edit" :title="t('editOrder')" @click="editOrder" />
          </div>
          <!-- <div class="flex flex-col items-center w-6/12 gap-1 text-black">
            <horizontal-button
              isDanger
              faIcon="trash"
              :title="t('deleteOrder')"
              @click="deleteOrder"
            />
          </div> -->
        </div>
      </template>


      <template v-if="!order.paymentComplete">
        <div class="mt-1 flex flex-col items-center w-12/12 gap-1 text-black">
          <horizontal-button
            faIcon="money-bill-wave"
            :title="t('makePayment')"
            @click="handleMakePayment"
          />
        </div>
      </template>

      <div class="mt-1 flex flex-col items-center w-12/12 gap-1 text-black">
          <horizontal-button
            isDanger
            :title="t('returnOrder')"
            @click="handleReturnOrder"
          >
          <custom-icon style="width: 20px; height: 20px;">
            <i-return-sale />
          </custom-icon>
        </horizontal-button>
        </div>

      <template v-if="showPrintReceipt">
        <div class="flex flex-col items-center w-12/12 gap-1 text-black mt-1">
          <horizontal-button
            faIcon="print"
            :title="t('printOrder')"
            @click="$router.push($Route.SALES_ORDERS_ORDER_RECEIPT.replace(':id', order.id))"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { formatDinero, formatMoney } from '@/utils/money'
import { useI18n } from 'vue-i18n'
import { i18nOrderMessages, Order } from '@/models/sales/Order'
import { formatPostgresDate } from '@/utils/date'
import { orderDueOnTextClass } from '@/utils/orderDueOn'
import { ElMessage, ElMessageBox } from 'element-plus'

import HorizontalButton from '@/components/HorizontalButton.vue'
import OrderStatusCellRenderer from "@/views/sales/OrderStatusCellRenderer.vue";
import OrderTypeCellRenderer from "@/views/sales/OrderTypeCellRenderer.vue";
import OrderCarrierCellRenderer from "@/views/sales/OrderCarrierCellRenderer.vue";
import PaymentStatusCellRenderer
  from "@/views/sales/PaymentStatusCellRenderer.vue";
import InsurancePaymentStatusCellRenderer
  from "@/views/companies/insurances/InsurancePaymentStatusCellRenderer.vue";
import IReturnSale from '@/components/icons/sales/IReturnSale.vue'
import CustomIcon from '@/components/CustomIcon.vue'
import FulfillButton from '@/components/FulfillButton.vue'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  name: 'order-info',
  setup() {
    const { t } = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...i18nOrderMessages.en,
          print: 'Print Receipt',
          makePayment: 'Make a payment',
          paymentsMade: 'Total paid amount',
          moneyOwed: 'Money Owed',
          direct: 'Direct',
          editOrder: 'Edit Order',
          deleteOrder: 'Delete Order',
          deleteOrderConfirm: 'Are you sure?',
          canotEditDirectOrder: "You can't edit Direct order!",
          discount: 'Discount',
          orderNotes: 'Order Notes',
          canotEditOrderWithPayment: "Can't edit order that has payment made",
          shipping: "Shipping",
          insurance: "Insurance",
          insuranceTotalDue: "Insurance Total Due",
          insurancePaidAmount: "Insurance Paid Amount",
          carrier: "Carrier",
          canotEditReturnedOrder: "Can't edit order that is partially or fully returned",
          errPaymentIsComplete: 'Payments are already complete, nothing to pay',
          customer: 'Customer',
          returnOrder: 'Return Order',
          fulfill: 'Fulfill',
          fulfilledBy: 'Fulfilled By'
        },
        ar: {
          ...i18nOrderMessages.ar,
          print: 'اطبع الفاتورة',
          makePayment: 'تسديد مبلغ',
          paymentsMade: 'المبلغ المدفوع',
          moneyOwed: 'المبلغ المطلوب',
          direct: 'مباشر',
          editOrder: 'تعديل الطلب',
          deleteOrder: 'مسح الطلب',
          deleteOrderConfirm: 'هل انت متأكد؟',
          canotEditDirectOrder: 'لا يمكن تعديل الطلب المباشر!',
          discount: 'الخصم',
          orderNotes: 'ملاحظات الطلب',
          canotEditOrderWithPayment: "لا يمكن تعديل طلب حصل فيه دفع",
          shipping: "توصيل",
          insurance: "شركة التأمين",
          insuranceTotalDue: "مبلغ التأمين",
          insurancePaidAmount: "التأمين المدفوع",
          carrier: "شركة التوصيل",
          canotEditReturnedOrder: "لا يمكن تعديل طلب متسرجع جزئيا او كليا",
          errPaymentIsComplete: 'تم دفع المبلغ كاملا',
          customer: 'زبون',
          returnOrder: 'ترجيع الطلب',
          fulfill: 'تجهيز',
          fulfilledBy: 'تم التجهيز بواسطة'
        }
      }
    })

    return { t, formatPostgresDate }
  },
  props: {
    order: {
      type: Order,
      required: true
    },
    showPrintReceipt: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  components: {
    HorizontalButton,
    OrderStatusCellRenderer,
    PaymentStatusCellRenderer,
    OrderTypeCellRenderer,
    OrderCarrierCellRenderer,
    InsurancePaymentStatusCellRenderer,
    CustomIcon,
    IReturnSale,
    FulfillButton
  },

  computed: {
    shouldShowFulfillButton() {
      // Check if any items have been returned
      const hasReturnedItems = this.order.orderItems.some(item => item.returnedQuantity > 0);
      
      return useSettingsStore().getValue('order_fulfilling_module') === 'true' && 
             (this.order.orderType === 'delivery' || this.order.orderType === 'pick_up') &&
             (this.order.orderStatus === 'unprocessed' || this.order.orderStatus === 'processing') &&
             this.order.orderStatus !== 'returned' &&
             !hasReturnedItems;
    },
    dueOnClass() {
      return orderDueOnTextClass(this.order.invoice?.billDueDate, this.order.invoice?.totalBalanceAmount)
    }
  },

  methods: {
    formatDinero,
    formatMoney,
    calculateTotalItemDiscounts() {
      let total = 0;
      for (const item of this.order.orderItems) {
        total += (item.itemDiscountTotal || 0) * item.quantity;
      }
      return total;
    },
    calculateTotalBeforeDiscounts() {
      // Total before discounts = Customer Total + Total Discount
      const customerTotal = this.order.invoice?.totalDueAmount || 0;
      const totalDiscount = (this.order.invoice?.totalDiscountAmount || 0) + this.calculateTotalItemDiscounts();
      return customerTotal + totalDiscount;
    },
    handleMakePayment() {
      if (this.order.invoice.totalBalanceAmount === 0) {
        ElMessage.error(this.$t('errPaymentIsComplete'))
        return
      }
      this.$router.push(
        this.$Route.SALES_ORDERS_ORDER_PAYMENT.replace(':id', this.order.invoiceID as string)
      )
    },
    handleReturnOrder() {
      this.$router.push(
        this.$Route.SALES_ORDERS_RETURN_ORDER.replace(':orderID', this.order.id as string)
      )
    },
    handleStatusChanged(newStatus: string) {
      // Update the order status locally to reflect the change
      this.order.orderStatus = newStatus
    },
    getOrderType(order: Order) {
      switch (order.orderType) {
        case 'direct':
          return this.t('orderTypes.direct');
        case 'pick_up':
          return this.t('orderTypes.pickUp');
        case 'delivery':
          return this.t('orderTypes.delivery');
      }
    },
    getPaymentTerm(order: Order) {
      switch (order.invoice.paymentType) {
        case 'direct':
          return this.t('invoiceTypes.full');
        case 'layaway':
          return this.t('invoiceTypes.layaway');
        case 'credit_sale':
          return this.t('invoiceTypes.credit');
        case 'cash_on_delivery':
          return this.t('invoiceTypes.cashOnDelivery');
      }
    },
    editOrder() {
      // can't edit returned order
      let hasReturnedItem = false;
      this.order.orderItems.forEach(item => {
        if (item.returnedQuantity > 0) {
          hasReturnedItem = true;
        }
      });

      if (hasReturnedItem) {
        ElMessage.error(this.$t('canotEditReturnedOrder'))
        return
      }

       this.$router.push(
          this.$Route.SALES_ORDERS_ORDER_EDIT.replace(':id', this.order.id as string)
        )
    },
    async deleteOrder() {
      if (confirm(this.$t('deleteOrderConfirm'))) {
        try {
          await this.$http.delete(Order.ENDPOINT, { data: [this.order] })
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
          this.$router.replace(this.$Route.SALES_ORDERS)
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    }
  }
})
</script>

<style>
.custom-bold {
  font-weight: bolder;
}
</style>
